import { Component, EventEmitter, Input, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgbModal, NgbModalRef, NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { HttpHeaders } from '@angular/common/http';
import { OnlyNumberDirective } from '../../../directives/only-number.directive';
import { CommonHttpService } from '../../../utils/common-http.service';
import constant from '../../../app.constant';
import { Router } from '@angular/router';

@Component({
  selector: 'app-ondc-config',
  standalone: true,
  imports: [CommonModule, FormsModule, NgbModule, OnlyNumberDirective],
  templateUrl: './ondc-config.component.html',
  styleUrl: './ondc-config.component.scss'
})
export class OndcConfigComponent implements OnInit {

  @Input() clusterData: any;
  @Input() userId: any;
  @Input() megaCityId: any;
  @Output() ondcSaved = new EventEmitter<{ ondc_3pl_config: any[]; ondc_provider_config: any[]; modalRef: NgbModalRef | null }>();

  @ViewChild('ondcModal') ondcModalTemplate!: TemplateRef<any>;
  private ondcModalRef: NgbModalRef | null = null;

  ondcPopupDp: any = null;
  ondcPopupIndex: number = -1;
  ondcProvidersList: any[] = [];
  ondcConfigList: any[] = constant.ondcDpUrlList || [];
  ondcApiData: any = null;
  kmValidationErrors: (string | null)[] = [];

  get isAllOndcSelected(): boolean {
    return this.ondcProvidersList.length > 0 &&
      this.ondcProvidersList.every((p: any) => p.selected);
  }

  // Drives the "Select all" checkbox's dash state when only some are picked.
  get isSomeOndcSelected(): boolean {
    return this.ondcProvidersList.some((p: any) => p.selected) && !this.isAllOndcSelected;
  }

  constructor(
    private toaster: ToastrService,
    private ngbModalService: NgbModal,
    private http: CommonHttpService,
    private router: Router
  ) { }

  ngOnInit() {
    this.getOndcDpUrlList();
  }

  // ── ONDC Modal Methods ─────────────────────────────────────────────────

  getOndcDpUrlList() {
    const url = constant.Service.SwiggyConfig.BaseUrl + constant.Service.MainMaster.DeliveryPartnerWiseOrderTime.OndcDpUrlList;
    const headers = new HttpHeaders().set('apikey', constant.Service.SwiggyConfig.apikey);
    this.http.get(url, { headers }).subscribe({
      next: (response: any) => {
        this.ondcConfigList = response.data.filter((item: any) => item.status === 'Active');
      }
    });
  }

  openOndcPopup(dp: any, index: number): void {
    this.ondcPopupDp = dp;
    this.ondcPopupIndex = index;
    let param: any = {}

    if (this.router.url.includes('settings/cluster') && !this.router.url.includes('settings/clustercustomerwise')) {
      param.type = "Cluster"
      param.cluster_id = this.megaCityId || this.clusterData.cl_cluster_id

    } else if (this.router.url.includes('settings/clustercustomerwise')) {
      param.type = "Own Cluster"
      param.cluster_id = this.megaCityId || this.clusterData.cl_cluster_id
      param.user_id = this.clusterData.userId
    } else {
      param.type = this.clusterData.apply_dp_setup
      param.user_id = this.clusterData.userId
    }
    param.status = "Active"
    if (this.megaCityId) {
      param.citywise = 'true'
    }
    this.http.postData(constant.Service.MainMaster.BaseUrl + constant.Service.MainMaster.DeliveryPartnerWiseOrderTime.ondcgetId, param).subscribe((ondcDetails: any) => {
      this.ondcApiData = ondcDetails?.data;

      let existingUrls: string[];
      let savedProviderConfig: any[];

      if (Array.isArray(this.ondcApiData) && this.ondcApiData.length > 0) {
        existingUrls = this.ondcApiData.map((item: any) => item.name);
        savedProviderConfig = this.ondcApiData.map((item: any) => ({
          lsp_subscriber_id: item.ondc_tpd_access,
          min_km: item.min_km,
          max_km: item.max_km,
          max_order_value: item.max_order_value,
          status: item.status
        }));
      } else {
        existingUrls = ('').split(',').map((s: string) => s.trim()).filter(Boolean);
        savedProviderConfig = this.clusterData?.ondc_provider_config || [];
      }

      this.ondcProvidersList = this.ondcConfigList.map((p: any) => {
        const saved = savedProviderConfig.find((s: any) => s.lsp_subscriber_id === p.lsp_subscriber_id);
        return {
          name: p.lsp_name,
          url: p.lsp_subscriber_id,
          logo: p.logo || null,
          selected: saved?.status === 'Active',
          // selected: existingUrls.includes(p.lsp_subscriber_id),
          min_km: saved?.min_km ?? null,
          max_km: saved?.max_km ?? null,
          max_order_value: saved?.max_order_value ?? null,
          status: saved?.status ?? 'Inactive'
        };
      });

      this.ondcModalRef = this.ngbModalService.open(this.ondcModalTemplate, {
        scrollable: true,
        // Sized in components/_modal.scss: a compact dialog on tablet/desktop,
        // a full-screen sheet on phones. Deliberately not `myCustomModalClass`,
        // whose width lives in the Map screen's stylesheet and so changed
        // (1140px vs 1600px) depending on whether Map had been visited.
        windowClass: 'adl-ondc-config-modal',
        centered: true,
        size: 'xl',
        backdrop: 'static',
        keyboard: false
      });
    });
  }

  closeOndcPopup(): void {
    this.ondcModalRef?.dismiss();
    this.ondcModalRef = null;
    this.ondcPopupDp = null;
    this.ondcPopupIndex = -1;
  }

  toggleSelectAllOndc(): void {
    const newVal = !this.isAllOndcSelected;
    this.ondcProvidersList.forEach((p: any, i: number) => {
      p.selected = newVal;
      if (!newVal) {
        p.max_order_value = null;
        p.min_km = null;
        p.max_km = null;
        this.kmValidationErrors[i] = null;
      }
    });
  }

  toggleProvider(provider: any, i: number): void {
    provider.selected = !provider.selected;
    if (!provider.selected) {
      provider.max_order_value = null;
      provider.min_km = null;
      provider.max_km = null;
      this.kmValidationErrors[i] = null;
    }
  }

  clearIfDeselected(provider: any, i: number): void {
    if (!provider.selected) {
      provider.max_order_value = null;
      provider.min_km = null;
      provider.max_km = null;
      this.kmValidationErrors[i] = null;
    }
  }

  getSelectedCount(): number {
    return this.ondcProvidersList.filter((p: any) => p.selected).length;
  }

  /** Prevents typing beyond allowed digits: 'single' = 1 digit (Min KM), 'double' = 2 digits (Max KM). */
  // restrictSingleDigit(event: KeyboardEvent, type: 'single' | 'double'): void {
  //   const input = event.target as HTMLInputElement;
  //   const maxLength = type === 'double' ? 2 : 1;
  //   const isDigit = event.key >= '0' && event.key <= '9';
  //   if (isDigit && input.value.length >= maxLength) {
  //     event.preventDefault();
  //   }
  // }
  restrictSingleDigit(event: KeyboardEvent, type: 'single' | 'double'): void {
    const input = event.target as HTMLInputElement;

    const allowedKeys = [
      'Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'
    ];

    if (allowedKeys.includes(event.key)) {
      return;
    }

    // Allow only one decimal point
    if (event.key === '.') {
      if (input.value.includes('.')) {
        event.preventDefault();
      }
      return;
    }

    if (!/^\d$/.test(event.key)) {
      event.preventDefault();
    }
  }


  validateKmRange(i: number): boolean {
    const provider = this.ondcProvidersList[i];
    const minKm = provider?.min_km;
    const maxKm = provider?.max_km;
    const providerName = provider?.name || 'Provider';

    this.kmValidationErrors[i] = null;

    const minKmEmpty = minKm === null || minKm === undefined || minKm === '';
    const maxKmEmpty = maxKm === null || maxKm === undefined || maxKm === '';

    if (!minKmEmpty && maxKmEmpty) {
      this.kmValidationErrors[i] = `${providerName}: Maximum KM is required.`;
      return false;
    }

    if (minKmEmpty && !maxKmEmpty) {
      this.kmValidationErrors[i] = `${providerName}: Minimum KM is required.`;
      return false;
    }

    if (minKmEmpty && maxKmEmpty) {
      return true;
    }

    const minKmNum = Number(minKm);
    const maxKmNum = Number(maxKm);

    if (!isNaN(minKmNum) && !isNaN(maxKmNum) && minKmNum > maxKmNum) {
      this.kmValidationErrors[i] = `Min KM (${minKmNum}) cannot be greater than Max KM (${maxKmNum}) for ${providerName}`;
      return false;
    }

    return true;
  }

  saveOndcConfig(): void {
    // Run inline validation for all rows first
    this.ondcProvidersList.forEach((_: any, i: number) => this.validateKmRange(i));
    if (this.kmValidationErrors.some(e => !!e)) return;

    // Validate each selected provider's KM range
    for (const provider of this.ondcProvidersList) {
      if (!provider.selected) continue;

      //     if (minKm > 5) {
      //   this.toaster.error(`Min KM must not be greater than 5 for ${provider.name}`);
      //   return;
      // }
      // if (maxKm > 50) {
      //   this.toaster.error(`Max KM must not be greater than 50 for ${provider.name}`);
      //   return;
      // }
      // if (minKm > maxKm) {
      //   this.toaster.error(`Min KM cannot be greater than Max KM for ${provider.name}`);
      //   return;
      // }
    }

    const selectedProviders = this.ondcProvidersList.filter((p: any) => p.selected);

    // if (selectedProviders.length === 0) {
    //   this.toaster.error('Please select at least one provider');
    //   return;
    // }

    const providerConfig = selectedProviders.map((p: any) => ({
      // name: p.url,

      lsp_subscriber_id: p.url,
      min_km: p.min_km !== null && p.min_km !== '' ? Number(p.min_km) : null,
      max_km: p.max_km !== null && p.max_km !== '' ? Number(p.max_km) : null,
      max_order_value: p.max_order_value !== null && p.max_order_value !== '' ? Number(p.max_order_value) : null,
    }));

    this.ondcSaved.emit({
      ondc_3pl_config: providerConfig,
      ondc_provider_config: providerConfig,
      modalRef: this.ondcModalRef
    });

  }

}
